// Adjustment dialog functions — Levels, Curves, Hue/Saturation, Color Balance,
// plus the adjustment-layer creation infrastructure.
// Extracted from main.ts to reduce its line count.

import type { AdjustmentParamValue } from "../core/adjustments/applyAdjustment";
import type { EditorState } from "./state/EditorState";
import type { FilterDialog } from "./ui/FilterDialog";
import type { AdjustmentKind } from "../core/document/Layer";
import { ApplyFilterCommand } from "../core/commands/ApplyFilterCommand";
import { NewLayerCommand } from "../core/commands/NewLayerCommand";
import { SetLayerPropertyCommand } from "../core/commands/SetLayerPropertyCommand";
import { createAdjustmentLayer } from "../core/document/Layer";
import { findLayer } from "../core/document/LayerUtils";
import { Levels } from "../core/filters/Levels";
import { Curves } from "../core/filters/Curves";
import { HueSaturation } from "../core/filters/HueSaturation";
import { ColorBalance } from "../core/filters/ColorBalance";
import { BlackWhite } from "../core/filters/BlackWhite";
import { Vibrance } from "../core/filters/Vibrance";

export interface AdjustmentDialogDeps {
  state: EditorState;
  filterDialog: FilterDialog;
  showTip: (msg: string) => void;
}

// ---------------------------------------------------------------------------
// Levels (Ctrl+L)
// ---------------------------------------------------------------------------

export async function promptLevels(deps: AdjustmentDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  const result = await deps.filterDialog.open("Levels", [
    { key: "inputBlack", label: "Input Black", type: "slider", default: 0, min: 0, max: 255 },
    { key: "gamma", label: "Gamma", type: "slider", default: 1.0, min: 0.1, max: 10.0, step: 0.01 },
    { key: "inputWhite", label: "Input White", type: "slider", default: 255, min: 0, max: 255 },
    { key: "outputBlack", label: "Output Black", type: "slider", default: 0, min: 0, max: 255 },
    { key: "outputWhite", label: "Output White", type: "slider", default: 255, min: 0, max: 255 },
  ]);
  if (!result) return;
  const doc = deps.state.doc;
  if (!doc) return;
  let id = doc.activeLayerId;
  if (!id) {
    const top = [...doc.layers].reverse().find((l) => l.kind === "raster");
    if (!top) return;
    id = top.id;
  }
  deps.state.execute(new ApplyFilterCommand(id, Levels, {
    inputBlack: result.inputBlack as number,
    gamma: result.gamma as number,
    inputWhite: result.inputWhite as number,
    outputBlack: result.outputBlack as number,
    outputWhite: result.outputWhite as number,
    channel: "composite",
  }, doc.selection ?? null));
}

// ---------------------------------------------------------------------------
// Curves (Ctrl+M)
// ---------------------------------------------------------------------------

export async function promptCurves(deps: AdjustmentDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  const result = await deps.filterDialog.open("Curves", [
    { key: "shadows", label: "Shadows (0-255)", type: "slider", default: 0, min: 0, max: 255 },
    { key: "midtones", label: "Midtones (0-255)", type: "slider", default: 128, min: 0, max: 255 },
    { key: "highlights", label: "Highlights (0-255)", type: "slider", default: 255, min: 0, max: 255 },
  ]);
  if (!result) return;
  const doc = deps.state.doc;
  if (!doc) return;
  let id = doc.activeLayerId;
  if (!id) {
    const top = [...doc.layers].reverse().find((l) => l.kind === "raster");
    if (!top) return;
    id = top.id;
  }
  const s = result.shadows as number;
  const m = result.midtones as number;
  const h = result.highlights as number;
  deps.state.execute(new ApplyFilterCommand(id, Curves, {
    points: [{ input: 0, output: s }, { input: 128, output: m }, { input: 255, output: h }],
    channel: "rgb",
  }, doc.selection ?? null));
}

// ---------------------------------------------------------------------------
// Hue / Saturation (Ctrl+U)
// ---------------------------------------------------------------------------

export async function promptHueSaturation(deps: AdjustmentDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  const result = await deps.filterDialog.open("Hue/Saturation", [
    { key: "hue", label: "Hue", type: "slider", default: 0, min: -180, max: 180 },
    { key: "saturation", label: "Saturation", type: "slider", default: 0, min: -100, max: 100 },
    { key: "lightness", label: "Lightness", type: "slider", default: 0, min: -100, max: 100 },
    { key: "colorize", label: "Colorize", type: "checkbox", default: false },
  ]);
  if (!result) return;
  const doc = deps.state.doc;
  if (!doc) return;
  let id = doc.activeLayerId;
  if (!id) {
    const top = [...doc.layers].reverse().find((l) => l.kind === "raster");
    if (!top) return;
    id = top.id;
  }
  deps.state.execute(new ApplyFilterCommand(id, HueSaturation, {
    hue: result.hue as number,
    saturation: result.saturation as number,
    lightness: result.lightness as number,
    colorize: result.colorize as boolean,
    colorizeHue: 0,
    colorizeSat: 25,
  }, doc.selection ?? null));
}

// ---------------------------------------------------------------------------
// Color Balance (Ctrl+B)
// ---------------------------------------------------------------------------

export async function promptColorBalance(deps: AdjustmentDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  const result = await deps.filterDialog.open("Color Balance", [
    { key: "shadowsCyanRed", label: "Shadows C/R", type: "slider", default: 0, min: -100, max: 100 },
    { key: "shadowsMagentaGreen", label: "Shadows M/G", type: "slider", default: 0, min: -100, max: 100 },
    { key: "shadowsYellowBlue", label: "Shadows Y/B", type: "slider", default: 0, min: -100, max: 100 },
    { key: "midtonesCyanRed", label: "Midtones C/R", type: "slider", default: 0, min: -100, max: 100 },
    { key: "midtonesMagentaGreen", label: "Midtones M/G", type: "slider", default: 0, min: -100, max: 100 },
    { key: "midtonesYellowBlue", label: "Midtones Y/B", type: "slider", default: 0, min: -100, max: 100 },
    { key: "highlightsCyanRed", label: "Highlights C/R", type: "slider", default: 0, min: -100, max: 100 },
    { key: "highlightsMagentaGreen", label: "Highlights M/G", type: "slider", default: 0, min: -100, max: 100 },
    { key: "highlightsYellowBlue", label: "Highlights Y/B", type: "slider", default: 0, min: -100, max: 100 },
    { key: "preserveLuminosity", label: "Preserve Luminosity", type: "checkbox", default: true },
  ]);
  if (!result) return;
  const doc = deps.state.doc;
  if (!doc) return;
  let id = doc.activeLayerId;
  if (!id) {
    const top = [...doc.layers].reverse().find((l) => l.kind === "raster");
    if (!top) return;
    id = top.id;
  }
  deps.state.execute(new ApplyFilterCommand(id, ColorBalance, {
    shadows: { cyanRed: result.shadowsCyanRed as number, magentaGreen: result.shadowsMagentaGreen as number, yellowBlue: result.shadowsYellowBlue as number },
    midtones: { cyanRed: result.midtonesCyanRed as number, magentaGreen: result.midtonesMagentaGreen as number, yellowBlue: result.midtonesYellowBlue as number },
    highlights: { cyanRed: result.highlightsCyanRed as number, magentaGreen: result.highlightsMagentaGreen as number, yellowBlue: result.highlightsYellowBlue as number },
    preserveLuminosity: result.preserveLuminosity as boolean,
  }, doc.selection ?? null));
}

// ---------------------------------------------------------------------------
// Vibrance
// ---------------------------------------------------------------------------

export async function promptVibrance(deps: AdjustmentDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  const result = await deps.filterDialog.open("Vibrance", [
    { key: "vibrance", label: "Vibrance", type: "slider", default: 0, min: -100, max: 100 },
    { key: "saturation", label: "Saturation", type: "slider", default: 0, min: -100, max: 100 },
  ]);
  if (!result) return;
  const doc = deps.state.doc;
  if (!doc) return;
  let id = doc.activeLayerId;
  if (!id) {
    const top = [...doc.layers].reverse().find((l) => l.kind === "raster");
    if (!top) return;
    id = top.id;
  }
  deps.state.execute(new ApplyFilterCommand(id, Vibrance, {
    vibrance: result.vibrance as number,
    saturation: result.saturation as number,
  }, doc.selection ?? null));
}

// ---------------------------------------------------------------------------
// Black & White
// ---------------------------------------------------------------------------

export function promptBlackWhite(deps: AdjustmentDialogDeps): void {
  if (!deps.state.doc) return;
  const doc = deps.state.doc;
  let id = doc.activeLayerId;
  if (!id) {
    const top = [...doc.layers].reverse().find((l) => l.kind === "raster");
    if (!top) return;
    id = top.id;
  }
  deps.state.execute(new ApplyFilterCommand(id, BlackWhite, {
    reds: 40, yellows: 60, greens: 40, cyans: 20, blues: 20, magentas: 80,
    tint: false, tintColor: { r: 0, g: 0, b: 0 }, tintAmount: 0,
  }, doc.selection ?? null));
}

// ---------------------------------------------------------------------------
// Adjustment layer creation helpers
// ---------------------------------------------------------------------------

export const ADJUSTMENT_LAYERS: { kind: AdjustmentKind; label: string }[] = [
  { kind: "brightness-contrast", label: "Brightness/Contrast" },
  { kind: "levels", label: "Levels" },
  { kind: "curves", label: "Curves" },
  { kind: "hue-saturation", label: "Hue/Saturation" },
  { kind: "color-balance", label: "Color Balance" },
  { kind: "black-white", label: "Black & White" },
  { kind: "photo-filter", label: "Photo Filter" },
  { kind: "gradient-map", label: "Gradient Map" },
  { kind: "invert", label: "Invert" },
  { kind: "posterize", label: "Posterize" },
  { kind: "threshold", label: "Threshold" },
  { kind: "solid-color", label: "Solid Color" },
  { kind: "exposure", label: "Exposure" },
  { kind: "vibrance", label: "Vibrance" },
  { kind: "channel-mixer", label: "Channel Mixer" },
  { kind: "color-lookup", label: "Color Lookup" },
  { kind: "selective-color", label: "Selective Color" },
];

export function getDefaultAdjustmentParams(kind: AdjustmentKind): Record<string, AdjustmentParamValue> {
  switch (kind) {
    case "brightness-contrast": return { brightness: 0, contrast: 0 };
    case "levels": return { inputBlack: 0, gamma: 1, inputWhite: 255, outputBlack: 0, outputWhite: 255 };
    case "curves": return { points: JSON.stringify([{ in: 0, out: 0 }, { in: 255, out: 255 }]) };
    case "hue-saturation": return { hue: 0, saturation: 0, lightness: 0, colorize: false };
    case "color-balance": return { shadowsCyanRed: 0, shadowsMagentaGreen: 0, shadowsYellowBlue: 0, midtonesCyanRed: 0, midtonesMagentaGreen: 0, midtonesYellowBlue: 0, highlightsCyanRed: 0, highlightsMagentaGreen: 0, highlightsYellowBlue: 0 };
    case "black-white": return { reds: 40, yellows: 60, greens: 40, cyans: 20, blues: 20, magentas: 80 };
    case "photo-filter": return { density: 25, preserveLuminosity: true };
    case "gradient-map": return { dither: false, reverse: false };
    case "invert": return {};
    case "posterize": return { levels: 4 };
    case "threshold": return { level: 128 };
    case "solid-color": return { color: JSON.stringify({ r: 128, g: 128, b: 128 }), opacity: 100 };
    case "exposure": return { exposure: 0, offset: 0, gamma: 1 };
    case "vibrance": return { vibrance: 0, saturation: 0 };
    case "channel-mixer": return {
      red:   { red: 100, green: 0, blue: 0, constant: 0 },
      green: { red: 0, green: 100, blue: 0, constant: 0 },
      blue:  { red: 0, green: 0, blue: 100, constant: 0 },
      monochrome: false,
    };
    case "color-lookup": return { lut: null, opacity: 1 };
    case "selective-color": return {
      reds:     { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      yellows:  { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      greens:   { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      cyans:    { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      blues:    { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      magentas: { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      whites:   { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      neutrals: { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      blacks:   { cyan: 0, magenta: 0, yellow: 0, black: 0 },
      method: "relative",
    };
    default: return {};
  }
}

export function addAdjustmentLayer(deps: AdjustmentDialogDeps, kind: AdjustmentKind): void {
  if (!deps.state.doc) return;
  const params = getDefaultAdjustmentParams(kind);
  const layer = createAdjustmentLayer(kind, params as Record<string, number | string | boolean>);
  deps.state.execute(new NewLayerCommand(layer));
}

export function editAdjustmentLayer(deps: AdjustmentDialogDeps, id: string): void {
  if (!deps.state.doc) return;
  const layer = findLayer(deps.state.doc.layers, id);
  if (!layer || layer.kind !== "adjustment") return;
  const adj = layer as import("../core/document/Layer").AdjustmentLayer;
  const fields = getAdjustmentDialogFields(adj.adjustment);
  if (fields.length === 0) return;

  const label = ADJUSTMENT_LAYERS.find(a => a.kind === adj.adjustment)?.label ?? "Adjustment";
  deps.filterDialog.open(label, fields).then((result) => {
    if (!result || !deps.state.doc) return;
    const newParams: Record<string, number | string | boolean> = {};
    for (const field of fields) {
      const val = result[field.key];
      if (val !== undefined) {
        newParams[field.key] = val as number | string | boolean;
      }
    }
    deps.state.execute(new SetLayerPropertyCommand(id, { params: newParams }));
  });
}

type FieldDef = {
  key: string;
  label: string;
  type: "slider" | "dropdown" | "checkbox" | "color";
  default: number | string | boolean;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: Array<{ value: string; label: string }>;
};

export function getAdjustmentDialogFields(kind: AdjustmentKind): FieldDef[] {
  switch (kind) {
    case "brightness-contrast":
      return [
        { key: "brightness", label: "Brightness", type: "slider", default: 0, min: -100, max: 100 },
        { key: "contrast", label: "Contrast", type: "slider", default: 0, min: -100, max: 100 },
      ];
    case "levels":
      return [
        { key: "inputBlack", label: "Input Black", type: "slider", default: 0, min: 0, max: 255 },
        { key: "gamma", label: "Gamma", type: "slider", default: 1, min: 0.1, max: 10 },
        { key: "inputWhite", label: "Input White", type: "slider", default: 255, min: 0, max: 255 },
        { key: "outputBlack", label: "Output Black", type: "slider", default: 0, min: 0, max: 255 },
        { key: "outputWhite", label: "Output White", type: "slider", default: 255, min: 0, max: 255 },
      ];
    case "hue-saturation":
      return [
        { key: "hue", label: "Hue", type: "slider", default: 0, min: -180, max: 180 },
        { key: "saturation", label: "Saturation", type: "slider", default: 0, min: -100, max: 100 },
        { key: "lightness", label: "Lightness", type: "slider", default: 0, min: -100, max: 100 },
        { key: "colorize", label: "Colorize", type: "checkbox", default: false },
      ];
    case "color-balance":
      return [
        { key: "shadowsCyanRed", label: "Shadows C/R", type: "slider", default: 0, min: -100, max: 100 },
        { key: "shadowsMagentaGreen", label: "Shadows M/G", type: "slider", default: 0, min: -100, max: 100 },
        { key: "shadowsYellowBlue", label: "Shadows Y/B", type: "slider", default: 0, min: -100, max: 100 },
        { key: "midtonesCyanRed", label: "Midtones C/R", type: "slider", default: 0, min: -100, max: 100 },
        { key: "midtonesMagentaGreen", label: "Midtones M/G", type: "slider", default: 0, min: -100, max: 100 },
        { key: "midtonesYellowBlue", label: "Midtones Y/B", type: "slider", default: 0, min: -100, max: 100 },
        { key: "highlightsCyanRed", label: "Highlights C/R", type: "slider", default: 0, min: -100, max: 100 },
        { key: "highlightsMagentaGreen", label: "Highlights M/G", type: "slider", default: 0, min: -100, max: 100 },
        { key: "highlightsYellowBlue", label: "Highlights Y/B", type: "slider", default: 0, min: -100, max: 100 },
      ];
    case "black-white":
      return [
        { key: "reds", label: "Reds", type: "slider", default: 40, min: -100, max: 100 },
        { key: "yellows", label: "Yellows", type: "slider", default: 60, min: -100, max: 100 },
        { key: "greens", label: "Greens", type: "slider", default: 40, min: -100, max: 100 },
        { key: "cyans", label: "Cyans", type: "slider", default: 20, min: -100, max: 100 },
        { key: "blues", label: "Blues", type: "slider", default: 20, min: -100, max: 100 },
        { key: "magentas", label: "Magentas", type: "slider", default: 80, min: -100, max: 100 },
      ];
    case "exposure":
      return [
        { key: "exposure", label: "Exposure", type: "slider", default: 0, min: -20, max: 20 },
        { key: "offset", label: "Offset", type: "slider", default: 0, min: -1, max: 1 },
        { key: "gamma", label: "Gamma", type: "slider", default: 1, min: 0.1, max: 10 },
      ];
    case "vibrance":
      return [
        { key: "vibrance", label: "Vibrance", type: "slider", default: 0, min: -100, max: 100 },
        { key: "saturation", label: "Saturation", type: "slider", default: 0, min: -100, max: 100 },
      ];
    case "posterize":
      return [{ key: "levels", label: "Levels", type: "slider", default: 4, min: 2, max: 255 }];
    case "threshold":
      return [{ key: "level", label: "Threshold Level", type: "slider", default: 128, min: 1, max: 255 }];
    case "photo-filter":
      return [{ key: "density", label: "Density", type: "slider", default: 25, min: 0, max: 100, unit: "%" }];
    default:
      return [];
  }
}
